/**
 * Générateur — Il détermine l'opposé d'une expression littérale.
 * Attendu ID : 208 | Niveau : 3ème | Domaine : Calcul littéral (d12, O8)
 *
 * Source doc prof (NOMBRE ET CALCULS 3e, séance A7S1). Plan de cartes
 * auto-validé 2026-07-16 (journal TODO_EXERCISEUR_3E.md §6) :
 *
 *   « fondation »   — opposé d'un produit ≠ produit des opposés (2 trous
 *                     NUMÉRIQUES : le contre-exemple qui vaccine, Ex.1c)
 *   « oppose »      — l'opposé d'une expression (coefficients en trous)
 *   « parentheses » — supprimer les parenthèses ±( ) et réduire
 *   « egales »      — quelles expressions sont égales ? (classer)
 *   « crochets »    — chaînes ±( ) multiples (étoile)
 *
 * Adaptation : la démonstration rédigée (Ex.1a-b) est portée par le
 * contre-exemple NUMÉRIQUE calculable ; les menus du doc → trous saisie de
 * coefficients isolés (structure fixe, leçon 4e).
 */

import {
    randomChoice, randInt, shuffle, tex,
    makeTexteTrou, makeClasser,
} from '../../../utils';
import { MOINS, fmtAffine, fmtPoly, carre } from './litteral';

const ATTENDU_ID = 208;

const L_FONDATION = 'L\'opposé d\'un produit (le contre-exemple)';
const L_OPPOSE = 'Donner l\'opposé d\'une expression';
const L_PARENTHESES = 'Supprimer les parenthèses et réduire';
const L_EGALES = 'Regrouper les expressions égales';
const L_CROCHETS = 'Réduire une chaîne de parenthèses';

const opts = (label) => ({ niveau: '3eme', matiere: 'maths', attendu: ATTENDU_ID, label });

// ═════════ « fondation » — le contre-exemple numérique ═════════

const genFondationTrous = () => {
    let a; let b;
    do { a = randInt(2, 9); b = randInt(2, 9); } while (a === b);
    const lettre = randomChoice(['x', 'y', 'a', 't']);
    // L'item était purement NUMÉRIQUE (9 × 6 et (−9) × (−6)) alors que l'attendu
    // porte sur l'opposé d'une EXPRESSION LITTÉRALE : la 2ᵈᵉ ligne introduit la lettre.
    const phrase = `$\\text{L'opposé de } ${a} \\times ${b} \\text{ vaut } {{${-a * b}}}`
        + ` \\qquad \\text{l'opposé de } ${a}${lettre} \\text{ vaut } {{${-a}}}${lettre}`
        + ` \\qquad (-${a}) \\times (-${lettre}) \\text{ vaut } {{${a}}}${lettre}$`;
    const payload = makeTexteTrou(phrase,
        '<p>« L\'opposé d\'un produit est-il égal au produit des opposés ? » Vérifions sur un nombre, '
        + `puis avec la lettre ${lettre}.</p>`
        + '<p><b>Complète, puis dis ce que tu remarques.</b> Écris les nombres négatifs avec leur signe.</p>',
        { ...opts(L_FONDATION), mode: 'saisie' });
    payload._meta.params = { a, b };
    return payload;
};

// ═════════ « oppose » — coefficients de l'opposé en trous ═════════

const coefNonNul = (lo, hi) => { let c = 0; while (c === 0) c = randInt(lo, hi); return c; };

const genOpposeTrous = () => {
    if (randInt(0, 1)) {
        // Degré 1 : ax + b → opposé -ax - b (2 trous signés).
        // Au moins un coefficient de l'OPPOSÉ est négatif : quand a et b étaient
        // tous deux négatifs, les deux trous se remplissaient sans jamais avoir à
        // décider d'un signe (le « + » du gabarit suffisait).
        let a; let b;
        do { a = coefNonNul(-9, 9); b = coefNonNul(-9, 9); } while (a < 0 && b < 0);
        const phrase = `$\\text{Opposé : } {{${-a}}}x + {{${-b}}}$`;
        const payload = makeTexteTrou(phrase,
            `<p>On considère l'expression ${tex(fmtAffine(a, b))}.</p>`
            + '<p><b>Complète les coefficients de son opposé.</b> Écris chaque coefficient '
            + 'avec son signe s\'il est négatif.</p>',
            { ...opts(L_OPPOSE), mode: 'saisie' });
        payload._meta.params = { deg: 1, a, b };
        return payload;
    }
    // Degré 2 : ax² + bx + c → opposé (3 trous signés), au moins un coefficient
    // positif dans l'expression de départ → au moins un signe à écrire.
    let a; let b; let c;
    do {
        a = coefNonNul(-5, 5); b = coefNonNul(-9, 9); c = coefNonNul(-9, 9);
    } while (a < 0 && b < 0 && c < 0);
    const phrase = `$\\text{Opposé : } {{${-a}}}x^{2} + {{${-b}}}x + {{${-c}}}$`;
    const payload = makeTexteTrou(phrase,
        `<p>On considère l'expression ${tex(fmtPoly([[a, carre()], [b, 'x'], [c, '']]))}.</p>`
        + '<p><b>Complète les coefficients de son opposé.</b> Écris chaque coefficient '
        + 'avec son signe s\'il est négatif.</p>',
        { ...opts(L_OPPOSE), mode: 'saisie' });
    payload._meta.params = { deg: 2, a, b, c };
    return payload;
};

// ═════════ « parentheses » — supprimer ±( ) et réduire ═════════

const genParenthesesTrous = () => {
    let a1; let b1; let a2; let b2; let signe; let k; let c;
    for (let essai = 0; essai < 60; essai++) {
        a1 = coefNonNul(-9, 9); b1 = coefNonNul(-9, 9);
        a2 = coefNonNul(-9, 9); b2 = coefNonNul(-9, 9);
        signe = randomChoice(['+', '-']);
        k = signe === '+' ? a1 + a2 : a1 - a2;
        c = signe === '+' ? b1 + b2 : b1 - b2;
        if (k !== 0 && c !== 0) break;
        k = 0;
    }
    if (!k) { a1 = 4; b1 = 0; a2 = -8; b2 = 5; signe = '+'; k = -4; c = 5; } // repli (doc Ex.2 E)
    const gauche = `${fmtAffine(a1, b1)} ${signe === '+' ? '+' : '-'} \\left(${fmtAffine(a2, b2)}\\right)`;
    const phrase = `$E = {{${k}}}x + {{${c}}}$`;
    const payload = makeTexteTrou(phrase,
        `<p>On pose ${tex(`E = ${gauche}`)}.</p>`
        + `<p>Le « ${signe === '+' ? '+ ( )' : `${MOINS} ( )`} » ${signe === '+' ? 'ne change aucun signe' : 'change TOUS les signes de la parenthèse'}. `
        + '<b>Supprime les parenthèses puis réduis.</b> Écris les coefficients avec leur signe.</p>',
        { ...opts(L_PARENTHESES), mode: 'saisie' });
    payload._meta.params = { a1, b1, a2, b2, signe, k, c };
    return payload;
};

// ═════════ « egales » — regrouper les expressions égales (classer) ═════════

// Habille kx + c en « (ax + b) ± (a'x + b') », avec ou sans parenthèses utiles.
const habiller = (k, c, vus) => {
    for (let essai = 0; essai < 60; essai++) {
        const a1 = coefNonNul(-9, 9); const b1 = randInt(-9, 9);
        if (a1 === k) continue;                            // seconde parenthèse jamais constante
        const forme = randomChoice(['plus_paren', 'moins_paren', 'moins_sans']);
        let txt;
        if (forme === 'plus_paren') {
            txt = tex(`\\left(${fmtAffine(a1, b1)}\\right) + \\left(${fmtAffine(k - a1, c - b1)}\\right)`);
        } else if (forme === 'moins_paren') {
            txt = tex(`\\left(${fmtAffine(a1, b1)}\\right) - \\left(${fmtAffine(a1 - k, b1 - c)}\\right)`);
        } else {
            // ax + b − a'x + b'' SANS parenthèses (le − ne porte que sur a'x)
            if (a1 - k < 2 || c - b1 <= 0) continue;       // structure lisible seulement (pas de « 1x »)
            txt = tex(`${fmtAffine(a1, b1)} - ${a1 - k}x + ${c - b1}`);
        }
        if (!vus.has(txt)) { vus.add(txt); return txt; }
    }
    return null;
};

const genEgalesClasser = () => {
    // 3 formes réduites distinctes, 2 habillages chacune.
    let cibles;
    for (let essai = 0; essai < 40; essai++) {
        cibles = [];
        const kSet = new Set(); const paires = new Set();
        while (cibles.length < 3) {
            const k = coefNonNul(-7, 7); const c = randInt(-8, 8);
            const cle = `${k}|${c}`;
            if (paires.has(cle) || kSet.has(k)) continue;   // pentes distinctes → jamais égales
            paires.add(cle); kSet.add(k);
            cibles.push({ k, c });
        }
        break;
    }
    const vus = new Set();
    const categories = [];
    for (const { k, c } of cibles) {
        const items = [];
        for (let i = 0; i < 2; i++) {
            const h = habiller(k, c, vus);
            if (h) items.push(h);
        }
        if (items.length < 2) return genEgalesClasser();
        categories.push({ name: `Égale à ${tex(fmtAffine(k, c))}`, items });
    }
    const payload = makeClasser(categories,
        '<p>Chaque expression se réduit à l\'une des trois formes proposées.</p>'
        + '<p><b>Classe les expressions selon leur forme réduite.</b></p>', opts(L_EGALES));
    payload._meta.params = { cibles };
    return payload;
};

// ═════════ « crochets » — chaînes ±( ) (étoile) ═════════

const genCrochetsTrous = () => {
    const c1 = randInt(2, 15); const c2 = randInt(2, 9); const c3 = randInt(2, 15); const c4 = randInt(2, 9);
    const motif = randomChoice(['p1', 'p2', 'p3']);
    let gauche; let k; let cst;
    if (motif === 'p1') {
        // c1 − (c2 − a) + c3 + (a − c4) = 2a + (c1 − c2 + c3 − c4)
        gauche = `${c1} - \\left(${c2} - a\\right) + ${c3} + \\left(a - ${c4}\\right)`;
        k = 2; cst = c1 - c2 + c3 - c4;
    } else if (motif === 'p2') {
        // c1 + (c2 − a) − c3 − (a − c4) = −2a + (c1 + c2 − c3 + c4)
        gauche = `${c1} + \\left(${c2} - a\\right) - ${c3} - \\left(a - ${c4}\\right)`;
        k = -2; cst = c1 + c2 - c3 + c4;
    } else {
        // c1 − (a + c2) − (a − c3) = −2a + (c1 − c2 + c3)
        gauche = `${c1} - \\left(a + ${c2}\\right) - \\left(a - ${c3}\\right)`;
        k = -2; cst = c1 - c2 + c3;
    }
    if (cst === 0) return genCrochetsTrousRepli();
    const phrase = `$A = {{${k}}}a + {{${cst}}}$`;
    const payload = makeTexteTrou(phrase,
        `<p>On pose ${tex(`A = ${gauche}`)}.</p>`
        + '<p><b>Réduis A.</b> Écris les coefficients avec leur signe.</p>',
        { ...opts(L_CROCHETS), mode: 'saisie' });
    payload._meta.params = { motif, k, cst };
    return payload;
};

// Repli déterministe (constante nulle rare) : le motif du doc, A = a + b + 9
// ramené à une lettre : 7 − (2 − a) + 9 + (a − 5) = 2a + 9.
const genCrochetsTrousRepli = () => {
    const phrase = '$A = {{2}}a + {{9}}$';
    const payload = makeTexteTrou(phrase,
        `<p>On pose ${tex('A = 7 - \\left(2 - a\\right) + 9 + \\left(a - 5\\right)')}.</p>`
        + '<p><b>Réduis A.</b> Écris les coefficients avec leur signe.</p>',
        { ...opts(L_CROCHETS), mode: 'saisie' });
    payload._meta.params = { motif: 'repli', k: 2, cst: 9 };
    return payload;
};

// ─── Registre ────────────────────────────────────────────────
const GENERATORS = {
    fondation_texte_trou_saisie: genFondationTrous,
    oppose_texte_trou_saisie: genOpposeTrous,
    parentheses_texte_trou_saisie: genParenthesesTrous,
    egales_classer: genEgalesClasser,
    crochets_texte_trou_saisie: genCrochetsTrous,
};

const FORMAT_LABELS = { texte_trou_saisie: 'Saisie trous', classer: 'Classer' };
const buildFormats = (prefix) =>
    Object.keys(GENERATORS).filter(k => k.startsWith(`${prefix}_`))
        .map(k => ({ key: k, label: FORMAT_LABELS[k.slice(prefix.length + 1)] || k }));

export const CATEGORIES = [
    { id: 'fondation', label: L_FONDATION, formatsList: buildFormats('fondation') },
    { id: 'oppose', label: L_OPPOSE, formatsList: buildFormats('oppose') },
    { id: 'parentheses', label: L_PARENTHESES, formatsList: buildFormats('parentheses') },
    { id: 'egales', label: L_EGALES, formatsList: buildFormats('egales') },
    { id: 'crochets', label: L_CROCHETS, formatsList: buildFormats('crochets') },
];

export const generate = (typeExercice) => {
    let key;
    if (typeExercice && GENERATORS[typeExercice]) key = typeExercice;
    else if (typeExercice) {
        const matching = Object.keys(GENERATORS).filter(k => k.startsWith(typeExercice) || k.includes(typeExercice));
        key = matching.length ? randomChoice(matching) : randomChoice(Object.keys(GENERATORS));
    } else key = randomChoice(Object.keys(GENERATORS));
    const payload = GENERATORS[key]();
    payload._meta.generatorKey = key;
    return payload;
};
